/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { AppTabs } from "./app-tabs.js";

interface Tab {
	value: string;
	label: string;
}

const MIN_WIDTH = 320;
const DEFAULT_WIDTH = 400;
const STORAGE_KEY = "stride.sidePanel.width";
const KEYBOARD_STEP = 16;

function clampWidth(width: number): number {
	const max = Math.max(MIN_WIDTH, Math.round(window.innerWidth * 0.6));
	return Math.min(max, Math.max(MIN_WIDTH, width));
}

function storedWidth(): number {
	const raw = Number(localStorage.getItem(STORAGE_KEY));
	return clampWidth(Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_WIDTH);
}

const styles = css`
	:host {
		display: flex;
		flex: 0 0 auto;
		height: 100%;
		min-height: 0;
	}

	:host(:not([open])) {
		display: none;
	}

	@media (max-width: 767px) {
		:host {
			display: none !important;
		}
	}

	.resizer {
		align-self: stretch;
		background: transparent;
		border: 0;
		cursor: col-resize;
		flex: 0 0 auto;
		padding: 0;
		position: relative;
		touch-action: none;
		width: 8px;
	}

	.resizer::after {
		background: var(--border, #e4e4e7);
		content: "";
		inset-block: 0;
		left: 50%;
		position: absolute;
		transform: translateX(-50%);
		transition: background-color 140ms ease;
		width: 1px;
	}

	.resizer:hover::after,
	.resizer:focus-visible::after {
		background: var(--primary, #18181b);
		width: 2px;
	}

	.resizer:focus-visible {
		outline: none;
	}

	.panel {
		background: var(--background, #ffffff);
		box-sizing: border-box;
		color: var(--foreground, #09090b);
		display: flex;
		flex: 1;
		flex-direction: column;
		min-height: 0;
		min-width: 0;
	}

	.header {
		align-items: center;
		border-bottom: 1px solid var(--border, #e4e4e7);
		display: flex;
		gap: 8px;
		justify-content: flex-start;
		height: 48px;
		padding: 0 12px;
	}

	.header-action {
		align-items: center;
		display: flex;
		margin-left: auto;
	}

	.body {
		flex: 1;
		min-height: 0;
		min-width: 0;
		overflow: auto;
	}
`;

export class AppSidePanel extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppSidePanel)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["open","data-open","tabs","data-tabs","activetab","data-active-tab","title","data-title"];

  #open = false;
  #tabs = [];
  #activeTab = "";
  #title = "";
  #width;
  #selectedTab;
  #openSet = false;
  #tabsSet = false;
  #activeTabSet = false;
  #titleSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #attr0;
  #attrLast0;
  #attrLast1;
  #lists = {};
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get open() {
    return this.#open;
  }

  set open(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#open, next)) return;
    this.#open = next;
    this.#openSet = true;
    this.#updateBindings('open');
  }

  get tabs() {
    return this.#tabs;
  }

  set tabs(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#tabs, next)) return;
    this.#tabs = next;
    this.#tabsSet = true;
    this.#updateBindings('tabs');
  }

  get activeTab() {
    return this.#activeTab;
  }

  set activeTab(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#activeTab, next)) return;
    this.#activeTab = next;
    this.#activeTabSet = true;
    this.#updateBindings('activeTab');
  }

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  #setWidth(value) {
    if (Object.is(this.#width, value)) return;
    this.#width = value;
    this.#updateBindings('width');
  }

  #setSelectedTab(value) {
    if (Object.is(this.#selectedTab, value)) return;
    this.#selectedTab = value;
    this.#updateBindings('selectedTab');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-open')) this.open = this.dataset.open;
    if (this.hasAttribute('data-tabs')) this.tabs = this.dataset.tabs;
    if (this.hasAttribute('data-active-tab')) this.activeTab = this.dataset.activeTab;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.open = props.open;
      this.tabs = props.tabs;
      this.activeTab = props.activeTab;
      this.title = props.title;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#openSet) this.open = this.dataset.open;
    if (!this.#tabsSet) this.tabs = this.dataset.tabs;
    if (!this.#activeTabSet) this.activeTab = this.dataset.activeTab;
    if (!this.#titleSet) this.title = this.dataset.title;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    this.#setWidth(storedWidth());
    this.style.width = `${this.#width}px`;
    const onResize = () => {
        this.#setWidth(clampWidth(this.#width));
        this.style.width = `${this.#width}px`;
    };
    window.addEventListener("resize", onResize);
    const onSelectTab = (event: Event) => {
        this.#setSelectedTab((event as CustomEvent<{
            value: string;
        }>).detail.value);
    };
    const onNestedTabChange = (event: Event) => {
        if (event.target === this)
            return;
        event.stopPropagation();
        this.#setSelectedTab((event as CustomEvent<{
            value: string;
        }>).detail.value);
        this.dispatchEvent(new CustomEvent("tab-change", { bubbles: true, composed: true, detail: { value: this.#selectedTab } }));
    };
    this.addEventListener("select-tab", onSelectTab);
    this.addEventListener("tab-change", onNestedTabChange);
    return () => {
        window.removeEventListener("resize", onResize);
        this.removeEventListener("select-tab", onSelectTab);
        this.removeEventListener("tab-change", onNestedTabChange);
    };
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
    this.#effect0();
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
    if (this.#effect0Cleanup) { this.#effect0Cleanup(); this.#effect0Cleanup = undefined; }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
      case 'data-open':
        this.open = newValue;
        break;
      case 'tabs':
      case 'data-tabs':
        this.tabs = newValue;
        break;
      case 'activetab':
      case 'data-active-tab':
        this.activeTab = newValue;
        break;
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
    }
  }

  #initState() {
    this.#width = DEFAULT_WIDTH;
    this.#selectedTab = this.#activeTab || this.#tabs[0]?.value || "";
  }

  #render() {
    const startDrag = (event: PointerEvent) => {
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = this.#width;
    document.body.style.userSelect = "none";
    const move = (moveEvent: PointerEvent) => {
        this.#setWidth(clampWidth(startWidth + (startX - moveEvent.clientX)));
        this.style.width = `${this.#width}px`;
    };
    const finish = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", finish);
        handle.removeEventListener("pointercancel", finish);
        document.body.style.userSelect = "";
        localStorage.setItem(STORAGE_KEY, String(this.#width));
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", finish);
    handle.addEventListener("pointercancel", finish);
};
    const onResizerKey = (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
        return;
    event.preventDefault();
    this.#setWidth(clampWidth(this.#width + (event.key === "ArrowLeft" ? KEYBOARD_STEP : -KEYBOARD_STEP)));
    this.style.width = `${this.#width}px`;
    localStorage.setItem(STORAGE_KEY, String(this.#width));
};
    return `<style>${this.#escText(styles)}</style><div class="resizer" role="separator" aria-orientation="vertical" aria-label="Resize panel" tabindex="0" data-argon="4788"></div><div class="panel"><div class="header"><div style="min-width:0;height:100%;display:flex;align-items:center"><app-tabs data-tabs="${this.#attrEsc(this.#tabs)}" data-value="${this.#attrEsc(this.#selectedTab)}" data-variant="line" data-argon-bind="0"><!--argon-text:0-->${""}<!--/argon-text:0--></app-tabs></div><div class="header-action"><slot name="header-action"></slot></div></div><div class="body"><!--argon-text:1-->${this.#tabs.map((tab) => `<slot name="${this.#escAttr(tab.value)}" style="${this.#escAttr((this.#selectedTab === tab.value) ? "" : "display:none")}"></slot>`).join("")}<!--/argon-text:1--></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #adoptList(start, end, htmls) {
    const specs = [];
    let node = start.nextSibling;
    for (const html of htmls) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nodes = [];
      const anchor = node;
      for (const fresh of template.content.childNodes) {
        if (!node || node === end || node.nodeType !== fresh.nodeType || (node.nodeType === 1 && node.localName !== fresh.localName)) return;
        nodes.push(node);
        node = node.nextSibling;
      }
      specs.push({ anchor, nodes });
    }
    if (node !== end) return;
    return specs.map(({ anchor, nodes }) => {
      const mark = document.createComment('argon-item');
      (anchor ?? end).before(mark);
      return [mark, ...nodes];
    });
  }

  #reconcileList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const htmls = items.map(String);
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { htmls, marks: nodes.map(nodes => nodes[0]) };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { htmls: [], marks: [] };
      }
    }
    const { htmls, marks } = list;
    if (items.length < htmls.length) {
      let node = marks[items.length];
      while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
      htmls.length = items.length;
      marks.length = items.length;
    }
    for (let i = 0; i < items.length; i++) {
      const html = String(items[i]);
      if (i < htmls.length) {
        const stop = i + 1 < marks.length ? marks[i + 1] : end;
        if (htmls[i] === html) {
          continue;
        }
        let node = marks[i].nextSibling;
        while (node && node !== stop) { const next = node.nextSibling; node.remove(); node = next; }
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        stop.before(template.content);
        htmls[i] = html;
      } else {
        const mark = document.createComment('argon-item');
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        end.before(mark, template.content);
        marks.push(mark);
        htmls.push(html);
      }
    }
  }
  #attrRaw(v) {
    return typeof v === 'object' ? JSON.stringify(v) : String(v);
  }

  #attrEsc(v) {
    return this.#attrRaw(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
  }

  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }

  #effect0Cleanup;
  #effect0() {
    if (this.#effect0Cleanup) this.#effect0Cleanup();
    const cleanup = (() => {
    this.toggleAttribute("open", this.#open);
})();
    this.#effect0Cleanup = typeof cleanup === 'function' ? cleanup : undefined;
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    const startDrag = (event: PointerEvent) => {
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = this.#width;
    document.body.style.userSelect = "none";
    const move = (moveEvent: PointerEvent) => {
        this.#setWidth(clampWidth(startWidth + (startX - moveEvent.clientX)));
        this.style.width = `${this.#width}px`;
    };
    const finish = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", finish);
        handle.removeEventListener("pointercancel", finish);
        document.body.style.userSelect = "";
        localStorage.setItem(STORAGE_KEY, String(this.#width));
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", finish);
    handle.addEventListener("pointercancel", finish);
};
    const onResizerKey = (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
        return;
    event.preventDefault();
    this.#setWidth(clampWidth(this.#width + (event.key === "ArrowLeft" ? KEYBOARD_STEP : -KEYBOARD_STEP)));
    this.style.width = `${this.#width}px`;
    localStorage.setItem(STORAGE_KEY, String(this.#width));
};
    if (prop === undefined || prop === 'tabs' || prop === 'selectedTab') {
      const __argonNext = `${""}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'tabs' || prop === 'selectedTab') {
      this.#reconcileList(1, this.#textStart1, this.#textEnd1, this.#tabs.map((tab) => `<slot name="${this.#escAttr(tab.value)}" style="${this.#escAttr((this.#selectedTab === tab.value) ? "" : "display:none")}"></slot>`));
    }
    if ((prop === undefined || prop === 'tabs') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#tabs)}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('data-tabs', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'selectedTab') && this.#attr0) {
      const __argonNext = `${this.#attrRaw(this.#selectedTab)}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr0.setAttribute('data-value', __argonNext);
      }
    }
    if (this.#hooksLive && (prop === 'open')) this.#effect0();
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '4788').forEach(el => this.#listen(el, '4788', 'pointerdown', (event: PointerEvent) => {
    event.preventDefault();
    const handle = event.currentTarget as HTMLElement;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = this.#width;
    document.body.style.userSelect = "none";
    const move = (moveEvent: PointerEvent) => {
        this.#setWidth(clampWidth(startWidth + (startX - moveEvent.clientX)));
        this.style.width = `${this.#width}px`;
    };
    const finish = () => {
        handle.releasePointerCapture(event.pointerId);
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", finish);
        handle.removeEventListener("pointercancel", finish);
        document.body.style.userSelect = "";
        localStorage.setItem(STORAGE_KEY, String(this.#width));
    };
    handle.addEventListener("pointermove", move);
    handle.addEventListener("pointerup", finish);
    handle.addEventListener("pointercancel", finish);
}));
    this.#eventTargets(scope, '4788').forEach(el => this.#listen(el, '4788', 'keydown', (event: KeyboardEvent) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight")
        return;
    event.preventDefault();
    this.#setWidth(clampWidth(this.#width + (event.key === "ArrowLeft" ? KEYBOARD_STEP : -KEYBOARD_STEP)));
    this.style.width = `${this.#width}px`;
    localStorage.setItem(STORAGE_KEY, String(this.#width));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
